import type {
  ReviewDetail,
  ReviewStreamEvent,
  ReviewTarget,
  ReviewTargetWire,
} from "./review.types"

/**
 * A target that can be started.
 *
 * Never run, run and failed, or — the case that is not obvious — finished and then emptied. The
 * evaluation conversation is an ordinary conversation of that account, so its owner can delete it
 * from the sidebar, and the report lives nowhere else. A completed target with no conversation
 * therefore holds nothing to read, and offering it again is the only way back.
 *
 * This mirrors `claim_target_for_evaluation` on the server, which admits exactly these three. If
 * that predicate changes, this has to change with it — a button the server refuses is worse than
 * no button.
 */
export const isEvaluatable = (target: ReviewTarget) =>
  target.status === "pending" ||
  target.status === "failed" ||
  (target.status === "completed" && target.conversationId == null)

/** A finished evaluation whose report is still there to open. */
export const isReadable = (target: ReviewTarget) =>
  target.status === "completed" && target.conversationId != null

export interface ReviewTally {
  total: number
  done: number
  running: number
  failed: number
  waiting: number
}

/**
 * What the list adds up to, for the line above it.
 *
 * A review carries up to twenty languages and each evaluation is its own minutes-long run, so
 * "is anything still going, and did anything fail" should not require reading twenty rows.
 *
 * `running` is the union of what the server says and what this tab has started, because the
 * server only learns a target is running when the next fetch lands — a row the reader just
 * clicked has to count immediately, or the line contradicts the row beneath it.
 *
 * A completed target whose report was deleted counts as waiting, not done. It is offered again,
 * and calling it done would say a report exists that does not.
 */
export const tally = (
  targets: readonly ReviewTarget[],
  running: ReadonlySet<string>
): ReviewTally =>
  targets.reduce<ReviewTally>(
    (counts, target) => {
      counts.total += 1
      if (running.has(target.id) || target.status === "in_progress") {
        counts.running += 1
      } else if (isReadable(target)) {
        counts.done += 1
      } else if (target.status === "failed") {
        counts.failed += 1
      } else {
        counts.waiting += 1
      }
      return counts
    },
    { total: 0, done: 0, running: 0, failed: 0, waiting: 0 }
  )

/** Whether a target is being evaluated, by the server's word. */
export const isRunning = (target: ReviewTarget) => target.status === "in_progress"

/**
 * Localized guidance for one failed evaluation.
 *
 * The server also supplies a safe English fallback, but this screen has a catalogue in every
 * supported language. Unknown and historical failures deliberately fall back to the existing
 * generic sentence rather than showing an internal or untranslated string.
 */
export const failureMessageKey = (target: ReviewTarget) => {
  switch (target.failureCode) {
    case "model_unavailable":
      return "review.failureModelUnavailable"
    case "timed_out":
      return "review.failureTimedOut"
    case "output_truncated":
      return "review.failureOutputTruncated"
    case "request_refused":
      return "review.failureRequestRefused"
    case "unreadable_reply":
      return "review.failureUnreadableReply"
    case "result_unavailable":
      return "review.failureResultUnavailable"
    case "abandoned":
      return "review.failureAbandoned"
    default:
      return "review.evaluateFailed"
  }
}

/** Whether anything in the review is still running — what decides if the page keeps re-reading. */
export const anyRunning = (targets: readonly ReviewTarget[]) => targets.some(isRunning)

/**
 * The detail with one target replaced by a newer reading of it.
 *
 * The same object back when the target is not in it, so a late event for a target that is gone
 * does not make every consumer re-render over nothing.
 */
export const withTarget = (detail: ReviewDetail, target: ReviewTarget): ReviewDetail => {
  if (!detail.targets.some((candidate) => candidate.id === target.id)) return detail
  return {
    ...detail,
    targets: detail.targets.map((candidate) =>
      candidate.id === target.id ? target : candidate
    ),
  }
}

/**
 * A target as the stream writes it, in this app's casing.
 *
 * Spelled out rather than run through `camelizeKeys`, so a field the server renames is a type
 * error here rather than `undefined` on screen.
 */
export const fromWire = (wire: ReviewTargetWire): ReviewTarget => ({
  id: wire.id,
  reviewId: wire.review_id,
  targetLanguage: wire.target_language,
  targetFileId: wire.target_file_id,
  targetFileName: wire.target_file_name,
  conversationId: wire.conversation_id,
  status: wire.status,
  failureCode: wire.failure_code,
  failureMessage: wire.failure_message,
  createDt: wire.create_dt,
  updateDt: wire.update_dt,
})

/** What this tab knows about one running evaluation, beyond its row. */
export interface LiveEvaluation {
  /** The report so far, as the model writes it. Empty until the first piece — and empty throughout
   * for a run the server cannot show, which then reports only the outcome. */
  text: string
  /** Whether the stream was lost and the row is being waited on instead. The text stays, so the
   * reader keeps what arrived; a re-attached stream replaces it whole. */
  lost: boolean
}

export const EMPTY_LIVE: LiveEvaluation = { text: "", lost: false }

/** One stream event folded into what the tab shows. Pure, so it can be tested without a socket. */
export const applyStreamEvent = (
  live: LiveEvaluation,
  event: ReviewStreamEvent
): LiveEvaluation => {
  switch (event.type) {
    case "snapshot":
      // The whole text, replacing rather than appending: a re-sync after falling behind and a
      // re-attach after a lost stream both carry everything so far.
      return { text: event.text, lost: false }
    case "delta":
      return { ...live, text: live.text + event.text }
    default:
      // The outcome is the row's business, not this tab's text.
      return live
  }
}
